import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import FinalCta from "@/pages/home/components/FinalCta";
import DomainSearch from "@/pages/dominios/components/DomainSearch";
import DomainCartForm from "@/pages/dominios/components/DomainCartForm";
import { domainTlds, domainBenefits, domainSteps, dominiosFaqs } from "@/mocks/dominios";

export default function Dominios() {
  return (
    <SiteLayout>
      <Seo
        title="Registro de Domínios .com.br, .com e mais | NXHOST"
        description="Registre o domínio da sua empresa com a NXHOST. Busque a disponibilidade de .com.br, .com, .store e outras extensões e fortaleça a sua presença digital."
        keywords="registro de domínio, domínio .com.br, registrar domínio, domínio próprio, endereço na internet"
        path="/dominios"
        titleEn="Domain Registration .com.br, .com and more | NXHOST"
        titleEs="Registro de Dominios .com.br, .com y más | NXHOST"
        descriptionEn="Register your company domain with NXHOST. Check availability for .com.br, .com, .store and other extensions and strengthen your online presence."
        descriptionEs="Registra el dominio de tu empresa con NXHOST. Consulta la disponibilidad de .com.br, .com, .store y otras extensiones y refuerza tu presencia digital."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Domínios", path: "/dominios" },
        ]}
        faqs={dominiosFaqs}
      />

      <PageHero
        badge="Domínios"
        badgeIcon="ri-global-line"
        title="O endereço profissional da sua empresa na internet."
        subtitle="Registre o domínio certo e fortaleça a presença digital do seu negócio. Busque o nome que combina com a sua marca e conte com a NXHOST em todo o processo."
        crumbs={[{ label: "Início", to: "/" }, { label: "Domínios" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500/15 text-primary-300">
                <i className="ri-global-line text-xl" aria-hidden="true"></i>
              </span>
              <div>
                <p className="text-xs uppercase tracking-wide text-background-400">
                  Seu domínio
                </p>
                <p className="font-heading text-lg font-bold text-background-50">
                  suaempresa.com.br
                </p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-xl font-bold text-background-50">Registro</p>
                <p className="mt-1 text-[11px] text-background-300">No nome da empresa</p>
              </div>
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="font-heading text-xl font-bold text-background-50">Suporte</p>
                <p className="mt-1 text-[11px] text-background-300">Em cada etapa</p>
              </div>
            </div>
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <SectionHeading
            badge="Busca de domínio"
            badgeIcon="ri-search-eye-line"
            title="Encontre o domínio ideal para o seu negócio."
            description="Digite o nome desejado e veja a disponibilidade nas principais extensões."
          />
          <div className="mt-10">
            <DomainSearch />
          </div>
        </div>
      </section>

      <DomainCartForm />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Extensões"
            badgeIcon="ri-price-tag-3-line"
            badgeTone="accent"
            title="Escolha a extensão que combina com a sua marca."
            description="Cada final tem um propósito. Se ficar em dúvida, a nossa equipe ajuda você a escolher."
          />
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {domainTlds.map((item, index) => (
              <Reveal key={item.tld} delay={index * 50} className="h-full">
                <div className="flex h-full flex-col items-center justify-center rounded-xl border border-background-200 bg-background-50 p-5 text-center transition-colors hover:border-primary-200">
                  <p className="font-heading text-lg font-bold text-foreground-950">{item.tld}</p>
                  <p className="mt-2 text-sm font-semibold text-foreground-900">
                    R$ {item.price}
                  </p>
                  <p className="mt-1 text-[11px] text-foreground-500">{item.promo}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-6 text-center text-xs text-foreground-500">
              Valores de referência por ano. As condições podem variar conforme a extensão escolhida.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Vantagens"
            badgeIcon="ri-shield-star-line"
            badgeTone="secondary"
            title="Registrar o seu domínio com a NXHOST."
            description="Mais do que registrar, acompanhamos a configuração para o seu domínio trabalhar a favor do seu negócio."
          />
          <div className="mt-12">
            <FeatureGrid items={domainBenefits} columns={3} accent="secondary" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como funciona"
            badgeIcon="ri-route-line"
            badgeTone="accent"
            title="Do nome escolhido ao domínio pronto para usar."
          />
          <div className="mt-12">
            <ProcessSteps steps={domainSteps} />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre domínios."
          />
          <div className="mt-12">
            <FaqAccordion items={dominiosFaqs} />
          </div>
        </div>
      </section>

      <FinalCta />
    </SiteLayout>
  );
}